import React, {useRef, useState} from "react";
import {Box, Button, CircularProgress, Link, Typography} from "@mui/material";
import {CloudUpload, Delete, InsertDriveFile} from "@mui/icons-material";
import Constants from "../../../../utils/constants.js";
import {authHeader} from "../../../../utils/headers.js";

/**
 * Upload d'un document vers S3 (modes opératoires / consignes) via l'endpoint
 * dédié de l'API. On stocke la CLÉ RELATIVE (ex. "consignes/xxx.pdf"), servie
 * ensuite par le proxy DocumentController (comme les photos d'intervention).
 *
 * Props :
 *  - value : clé relative courante (string)
 *  - onChange(path, originalName)
 *  - folder : 'modes_operatoires' | 'consignes'
 *  - accept : filtre input (def. PDF)
 *  - label
 */
export default function S3FileUpload({value, onChange, folder = "modes_operatoires", accept = "application/pdf", label = "Fichier"}) {
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState(null);
    const [name, setName] = useState(null);

    const handleFile = async (file) => {
        if (!file) return;
        setUploading(true);
        setError(null);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("folder", folder);

            const res = await fetch(Constants.API_ROUTE_DOCUMENT_UPLOAD, {
                method: "POST",
                headers: {Authorization: authHeader()},
                body: formData,
            });
            if (!res.ok) throw new Error("Échec de l'upload (" + res.status + ")");
            const data = await res.json();
            setName(data.originalName);
            onChange(data.path, data.originalName);
        } catch (e) {
            setError(e.message || "Erreur d'upload");
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box>
            <Typography variant="caption" color="textSecondary">{label}</Typography>
            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mt: 0.5}}>
                <Button
                    variant="outlined"
                    startIcon={uploading ? <CircularProgress size={16}/> : <CloudUpload/>}
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                >
                    {value ? "Remplacer" : "Téléverser"}
                </Button>

                {value && (
                    <>
                        <Link href={`${Constants.API_ASSETS_DOCUMENT}/${value}`} target="_blank" rel="noopener" sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <InsertDriveFile fontSize="small"/>
                            {name || "Voir le fichier"}
                        </Link>
                        <Button color="error" size="small" startIcon={<Delete/>} onClick={() => {
                            setName(null);
                            onChange(null, null);
                        }}>
                            Retirer
                        </Button>
                    </>
                )}

                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    hidden
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
            </Box>
            {error && <Typography variant="caption" color="error">{error}</Typography>}
        </Box>
    );
}
